import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { 
  RefreshCw, Plus, Search, ExternalLink, Trash2, UserPlus, UserMinus,
  Scissors, Sparkles, Palette, Eye, PartyPopper, GraduationCap,
  ChefHat, Heart, Plane, Leaf, ShoppingBag, LayoutDashboard, ClipboardList,
  Users, Edit2, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  scissors: Scissors, sparkles: Sparkles, palette: Palette, eye: Eye,
  "party-popper": PartyPopper, "graduation-cap": GraduationCap,
  "chef-hat": ChefHat, heart: Heart, plane: Plane, leaf: Leaf,
  "shopping-bag": ShoppingBag,
};

const CATEGORY_CONFIG = {
  hair: { name: "Hair", color: "#e83e8c", icon: "scissors" },
  makeup: { name: "Makeup", color: "#9c57f5", icon: "sparkles" },
  nails: { name: "Nails", color: "#f15a70", icon: "palette" },
  lashes: { name: "Lashes", color: "#774cda", icon: "eye" },
  events: { name: "Events", color: "#f9c434", icon: "party-popper" },
  instructors: { name: "Instructors", color: "#4a90d9", icon: "graduation-cap" },
  instructor: { name: "Instructors", color: "#4a90d9", icon: "graduation-cap" },
  culinary: { name: "Culinary", color: "#f28c28", icon: "chef-hat" },
  wellness: { name: "Wellness", color: "#75d6d6", icon: "heart" },
  travel: { name: "Travel", color: "#73b7ff", icon: "plane" },
  dispensary: { name: "Dispensary", color: "#60d680", icon: "leaf" },
  dispensaries: { name: "Dispensary", color: "#60d680", icon: "leaf" },
  retail: { name: "Retail", color: "#9950fa", icon: "shopping-bag" },
};

const getHeaders = () => {
  const token = localStorage.getItem('session_token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

const HostDashboardsTab = () => {
  const navigate = useNavigate();
  const [dashboards, setDashboards] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showAssign, setShowAssign] = useState(null);
  const [newDash, setNewDash] = useState({ name: '', category: 'hair' });
  const [assignEmail, setAssignEmail] = useState('');
  const [assignSearch, setAssignSearch] = useState('');
  const [assignResults, setAssignResults] = useState([]);
  const [assignSearching, setAssignSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [evtRes, intakeRes] = await Promise.all([
        fetch(`${API}/admin/event-org-links`, { headers: getHeaders() }),
        fetch(`${API}/admin/pilot-intakes`, { headers: getHeaders() }).catch(() => null),
      ]);

      if (evtRes.ok) {
        const data = await evtRes.json();
        setDashboards(data.events || []);
      }

      if (intakeRes?.ok) {
        const iData = await intakeRes.json();
        setIntakes(Array.isArray(iData) ? iData : iData.intakes || []);
      }
    } catch (err) {
      console.error('Failed to load dashboards:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredDashboards = dashboards.filter(d => {
    const cat = d.pilot_category || d.category || '';
    const matchSearch = !search || d.name?.toLowerCase().includes(search.toLowerCase()) || cat.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat === 'all' || cat === filterCat || cat + 's' === filterCat || cat.replace(/s$/, '') === filterCat;
    return matchSearch && matchCat;
  });

  const [createHostSearch, setCreateHostSearch] = useState('');
  const [createHostResults, setCreateHostResults] = useState([]);
  const [createHostEmail, setCreateHostEmail] = useState('');
  const [createSearching, setCreateSearching] = useState(false);

  const createDashboard = async () => {
    if (!newDash.name) { toast.error("Business name required"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/create-pilot-dashboard`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ name: newDash.name, category: newDash.category, host_email: createHostEmail || undefined })
      });
      if (res.ok) {
        toast.success("Dashboard created");
        setShowCreate(false);
        setNewDash({ name: '', category: 'hair' });
        setCreateHostSearch('');
        setCreateHostEmail('');
        setCreateHostResults([]);
        loadData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to create");
      }
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  // Search users for create dialog
  useEffect(() => {
    if (!showCreate || !createHostSearch || createHostSearch.length < 2) { setCreateHostResults([]); return; }
    const timer = setTimeout(async () => {
      setCreateSearching(true);
      try {
        const res = await fetch(`${API}/admin/users?search=${encodeURIComponent(createHostSearch)}&limit=8`, { headers: getHeaders() });
        if (res.ok) {
          const data = await res.json();
          setCreateHostResults(data.users || data || []);
        }
      } catch {}
      setCreateSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [createHostSearch, showCreate]);

  const deleteDashboard = async (eventId) => {
    if (!window.confirm("Delete this dashboard? This cannot be undone.")) return;
    try {
      const res = await fetch(`${API}/admin/events/${eventId}`, { method: 'DELETE', headers: getHeaders() });
      if (res.ok) { toast.success("Dashboard deleted"); loadData(); }
      else toast.error("Delete failed");
    } catch { toast.error("Delete failed"); }
  };

  const assignHost = async (eventId) => {
    if (!assignEmail) { toast.error("Email required"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/events/${eventId}/assign-host`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ email: assignEmail })
      });
      if (res.ok) {
        toast.success("Host assigned");
        setShowAssign(null);
        setAssignEmail('');
        loadData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to assign");
      }
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const removeHost = async (eventId) => {
    if (!window.confirm("Remove yourself as host? The dashboard will remain in your pilot dashboards.")) return;
    try {
      const res = await fetch(`${API}/admin/events/${eventId}/remove-host`, {
        method: 'POST', headers: getHeaders()
      });
      if (res.ok) { toast.success("Host removed"); loadData(); }
      else toast.error("Remove failed");
    } catch { toast.error("Failed"); }
  };

  // Search existing users for assign host
  const searchUsers = async (query) => {
    if (!query || query.length < 2) { setAssignResults([]); return; }
    setAssignSearching(true);
    try {
      const res = await fetch(`${API}/admin/users?search=${encodeURIComponent(query)}&limit=10`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAssignResults(data.users || data || []);
      }
    } catch (err) { console.error('Search failed:', err); }
    setAssignSearching(false);
  };

  // Debounced search
  useEffect(() => {
    if (!showAssign) return;
    const timer = setTimeout(() => searchUsers(assignSearch), 300);
    return () => clearTimeout(timer);
  }, [assignSearch, showAssign]);

  // Group by category
  const grouped = {};
  filteredDashboards.forEach(d => {
    const cat = d.pilot_category || d.category || 'other';
    const key = cat.replace(/s$/, '');
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(d);
  });

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
    </div>
  );

  return (
    <div data-testid="host-dashboards-tab">
      {/* Header Bar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search dashboards..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-64"
              data-testid="dashboard-search"
            />
          </div>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-40" data-testid="cat-filter">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(CATEGORY_CONFIG).filter(([k]) => !['instructor','dispensaries'].includes(k)).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-sm">{filteredDashboards.length} dashboards</Badge>
          <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-dashboard-btn">
            <Plus className="w-4 h-4 mr-1" /> New Dashboard
          </Button>
        </div>
      </div>

      {/* View Tabs */}
      <Tabs value="dashboards" className="mb-4">
        <TabsList>
          <TabsTrigger value="dashboards" className="gap-1.5">
            <LayoutDashboard className="w-3.5 h-3.5" /> Host Dashboards ({filteredDashboards.length})
          </TabsTrigger>
        </TabsList>

        {/* DASHBOARDS VIEW */}
        <TabsContent value="dashboards">
          {Object.keys(grouped).length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <LayoutDashboard className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No host dashboards found</p>
              <p className="text-sm">Create one or adjust your filters</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(grouped).sort().map(([catKey, items]) => {
                const cfg = CATEGORY_CONFIG[catKey] || CATEGORY_CONFIG[catKey + 's'] || { name: catKey, color: '#888', icon: 'sparkles' };
                const Icon = ICON_MAP[cfg.icon] || Sparkles;
                return (
                  <div key={catKey}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: cfg.color + '20', color: cfg.color }}>
                        <Icon className="w-3.5 h-3.5" />
                      </div>
                      <h3 className="font-semibold text-sm" style={{ color: cfg.color }}>{cfg.name}</h3>
                      <Badge variant="outline" className="text-xs">{items.length}</Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {items.map(d => {
                        const hostName = d.host_name || d.host_user_name || 'Unassigned';
                        return (
                          <Card key={d.event_id} className="hover:shadow-md transition-shadow" data-testid={`dashboard-card-${d.event_id}`}>
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{d.name || d.event_name || d.title || d.display_name || hostName}</p>
                                  <p className="text-xs text-gray-500">Host: {hostName}</p>
                                  {d.is_demo && <span className="px-1 py-0.5 text-[8px] font-bold bg-amber-100 text-amber-700 rounded uppercase">demo</span>}
                                </div>
                                <Badge className="text-[10px] ml-2" style={{ backgroundColor: cfg.color + '15', color: cfg.color, border: `1px solid ${cfg.color}30` }}>
                                  {d.status || 'active'}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-3">
                                <Users className="w-3 h-3" />
                                <span>{d.rsvp_count || 0} clients</span>
                                <span className="mx-1">|</span>
                                <span>ID: {d.event_id?.slice(0, 12)}...</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <Button size="sm" variant="outline" className="flex-1 text-xs h-7"
                                  onClick={() => navigate(`/host/v2/${d.event_id}`)}
                                  data-testid={`enter-dash-${d.event_id}`}>
                                  <ExternalLink className="w-3 h-3 mr-1" /> Enter
                                </Button>
                                <Button size="sm" variant="outline" className="text-xs h-7"
                                  onClick={() => setShowAssign(d.event_id)}
                                  data-testid={`assign-host-${d.event_id}`}>
                                  <UserPlus className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="outline" className="text-xs h-7"
                                  onClick={() => removeHost(d.event_id)}
                                  data-testid={`remove-host-${d.event_id}`}>
                                  <UserMinus className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-xs h-7 text-red-500 hover:text-red-700"
                                  onClick={() => deleteDashboard(d.event_id)}
                                  data-testid={`delete-dash-${d.event_id}`}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Dashboard Dialog */}
      <Dialog open={showCreate} onOpenChange={(o) => { setShowCreate(o); if (!o) { setCreateHostSearch(''); setCreateHostEmail(''); setCreateHostResults([]); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Host Dashboard</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Business / Organization Name</Label>
              <Input value={newDash.name} onChange={(e) => setNewDash(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Mia MUA, Luxe Nails Studio" data-testid="new-dash-name" />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={newDash.category} onValueChange={(v) => setNewDash(p => ({ ...p, category: v }))}>
                <SelectTrigger data-testid="new-dash-cat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_CONFIG).filter(([k]) => !['instructor','dispensaries'].includes(k)).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assign Host (optional)</Label>
              <Input value={createHostSearch} onChange={(e) => setCreateHostSearch(e.target.value)}
                placeholder="Search by name or email..." data-testid="create-host-search" />
              {createSearching && <p className="text-xs text-gray-400 mt-1">Searching...</p>}
              {createHostResults.length > 0 && (
                <div className="mt-2 max-h-36 overflow-y-auto border rounded-lg divide-y">
                  {createHostResults.map(u => (
                    <button key={u.user_id} className={`w-full text-left p-2 hover:bg-blue-50 flex items-center gap-2 text-sm ${createHostEmail === u.email ? 'bg-blue-100' : ''}`}
                      onClick={() => { setCreateHostEmail(u.email); setCreateHostSearch(u.name || u.email); setCreateHostResults([]); }}>
                      <Users className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium truncate text-xs">{u.name || 'No name'}</p>
                        <p className="text-[10px] text-gray-500 truncate">{u.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {createHostEmail && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Host: {createHostEmail}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createDashboard} disabled={saving} data-testid="create-dash-submit">
              {saving ? "Creating..." : "Create Dashboard"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Host Dialog */}
      <Dialog open={!!showAssign} onOpenChange={(o) => { if (!o) { setShowAssign(null); setAssignSearch(''); setAssignResults([]); setAssignEmail(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Host to Dashboard</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Search Existing Users</Label>
              <Input value={assignSearch} onChange={(e) => setAssignSearch(e.target.value)}
                placeholder="Search by name or email..." data-testid="assign-search" />
              {assignSearching && <p className="text-xs text-gray-400 mt-1">Searching...</p>}
              {assignResults.length > 0 && (
                <div className="mt-2 max-h-48 overflow-y-auto border rounded-lg divide-y">
                  {assignResults.map(u => (
                    <button key={u.user_id} className={`w-full text-left p-2.5 hover:bg-blue-50 flex items-center gap-2 text-sm transition-colors ${assignEmail === u.email ? 'bg-blue-100' : ''}`}
                      onClick={() => { setAssignEmail(u.email); setAssignSearch(u.name || u.email); setAssignResults([]); }}
                      data-testid={`assign-user-${u.user_id}`}>
                      <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium truncate">{u.name || 'No name'}</p>
                        <p className="text-xs text-gray-500 truncate">{u.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {assignEmail && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Selected: {assignEmail}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssign(null)}>Cancel</Button>
            <Button onClick={() => assignHost(showAssign)} disabled={saving || !assignEmail} data-testid="assign-host-submit">
              {saving ? "Assigning..." : "Assign Host"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HostDashboardsTab;
